<template>
  <MediaViewerModal
    :title="title"
    :download-url="downloadUrl"
    :media="photo"
    :loading="loading"
    :error="error"
    loading-message="Loading photo..."
    default-icon="fa-solid fa-image"
    :can-go-previous="canGoPrevious"
    :can-go-next="canGoNext"
    :go-previous="goPrevious"
    :go-next="goNext"
    @hide-modal="emit('hide-modal')"
    @retry="retryLoad"
  >
    <template #default="{ hiddenClass }">
      <img
        :key="photo.id"
        ref="imageRef"
        :src="imageUrl"
        alt="Photo"
        class="media-viewer-element"
        :class="hiddenClass"
        @load="onImageLoaded"
        @error="onImageError"
      />
    </template>
    <template #side-rail>
      <CaptionRail
        :item-id="photo.id"
        :caption="photo.caption"
        :open-mode="openMode"
        :readonly="readonly"
        @save="(text) => emit('save-caption', text)"
        @delete="emit('delete-caption')"
      />
    </template>
  </MediaViewerModal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";

import CaptionRail from "@/components/CaptionRail.vue";
import MediaViewerModal from "@/components/MediaViewerModal.vue";
import { Photo } from "@/typings/photo";

const props = withDefaults(
  defineProps<{
    photo: Photo;
    canGoPrevious?: boolean;
    canGoNext?: boolean;
    goPrevious?: () => void;
    goNext?: () => void;
    openMode?: "read" | "edit";
    readonly?: boolean;
  }>(),
  {
    canGoPrevious: false,
    canGoNext: false,
    goPrevious: undefined,
    goNext: undefined,
    openMode: "read",
    readonly: false
  }
);

const emit = defineEmits<{
  "hide-modal": [];
  "save-caption": [caption: string];
  "delete-caption": [];
}>();

const imageRef = ref<HTMLImageElement | null>(null);
const loading = ref(true);
const error = ref<string | null>(null);

// Reset loading state when photo changes, but skip if image is already cached
watch(
  () => props.photo.id,
  () => {
    error.value = null;
    const newUrl = props.photo.file?.url || "";
    if (newUrl) {
      // Check if image is already in browser cache
      const img = new Image();
      img.src = newUrl;
      if (img.complete && img.naturalWidth > 0) {
        // Image is cached, no need to show loading
        loading.value = false;
        return;
      }
    }
    loading.value = true;
  }
);

const title = computed(() => props.photo.name || "Photo Viewer");

const imageUrl = computed(() => {
  return props.photo.file?.url || "";
});

const downloadUrl = computed(() => {
  return props.photo.download_url || imageUrl.value;
});

function onImageLoaded() {
  loading.value = false;
  error.value = null;
}

function onImageError() {
  error.value = "Error loading photo. The file may be unavailable.";
  loading.value = false;
}

function retryLoad() {
  loading.value = true;
  error.value = null;
  const img = imageRef.value;
  if (img) {
    const currentSrc = img.src;
    img.src = "";
    img.src = currentSrc;
  }
}

defineExpose({
  loading,
  error,
  imageUrl,
  downloadUrl
});
</script>
